import {
  enforceMinimumPrecision,
  floatSafeMultiply,
  FormFields,
  useForm
} from '@sequencehq/utils'
import { greaterThan, required } from '@sequencehq/utils/validation'
import { usePricingEditorDomainContext } from 'common/drawers/PricingEditor/communication'
import {
  BillingFrequency,
  BillingType,
  PricingEditorReducerState
} from 'common/drawers/PricingEditor/domain'
import { defaultAvailableFrequenciesOptions } from 'common/drawers/PricingEditor/domain/pricingEditor.constants'
import { useMemo } from 'react'
import { useCommonFieldConfig } from '../common/useCommonFieldConfig'
import { useSyncFormValidity } from '../common/useSyncFormValidity'
import { commonPriceDetails } from '../common/ReadOnlyDetails/readOnlyDetails.utils'
import { getBillingFrequencyMultiplier } from 'common/drawers/PricingEditor/utils/getBillingFrequencyMultiplier'
import { pick } from 'lodash/fp'
import { dequal } from 'dequal'

type CommonFields =
  PricingEditorReducerState['data']['pricingEditorData']['common']
type StandardPriceFields =
  PricingEditorReducerState['data']['pricingEditorData']['STANDARD']

type StandardPriceFormFieldConfig = Omit<
  FormFields<CommonFields>,
  'pricingModel' | 'currency'
> &
  FormFields<StandardPriceFields> & {
    currency: FormFields<CommonFields>['currency'] & {
      hidden: boolean
    }
  }

type UseStandardPriceForm = () => {
  fieldsConfig: StandardPriceFormFieldConfig
  priceDetails: { label: string; value: string }[]
}

const availableBillingTypeOptions: { value: BillingType; label: string }[] = [
  { label: 'In arrears', value: 'IN_ARREARS' },
  { label: 'In advance', value: 'IN_ADVANCE' }
]

/**
 * The standard price has a bonus option, as the standard price pulls
 * double duty as the 'FIXED' and 'ONE_TIME' pricing model form. ONE_TIME
 * as a frequency will map to ONE_TIME as the model when going through the
 * external adapters.
 */
const availableFrequencies = [
  {
    value: 'ONE_TIME',
    label: 'Once'
  },
  ...defaultAvailableFrequenciesOptions
]

const adjustStandardPrice = (params: { ratio: number; price: string }) => {
  const pricePrecision = params.price.split('.')[1]?.length ?? 0
  const flatFeeTargetPrecision = Math.max(pricePrecision, 2)

  return enforceMinimumPrecision(2)(
    floatSafeMultiply(+params.price, params.ratio, flatFeeTargetPrecision)
  )
}

export const useStandardPriceForm: UseStandardPriceForm = () => {
  const pricingEditorContext = usePricingEditorDomainContext()

  const commonFields = useCommonFieldConfig<
    StandardPriceFields & CommonFields
  >()

  const {
    fields,
    queries: formQueries,
    mutators: formMutators
  } = useForm({
    value: {
      ...pricingEditorContext.queries.rawData.data.pricingEditorData.common,
      ...pricingEditorContext.queries.rawData.data.pricingEditorData.STANDARD
    },
    reinitializeOnValueChange: (params: {
      newData: StandardPriceFields & CommonFields
      prevData: (StandardPriceFields & CommonFields) | undefined
    }) => {
      const propertiesToCompare = pick(['productId', 'listPriceId'])
      return !dequal(
        propertiesToCompare(params.newData),
        propertiesToCompare(params.prevData)
      )
    },
    fieldConfiguration: [
      ...commonFields.fields,
      {
        property: 'price',
        validation: [required, greaterThan(0, "Price can't be zero")]
      },
      {
        property: 'billingFrequency',
        validation: [required],
        options: availableFrequencies
      },
      {
        property: 'billingType',
        validation: [required],
        options: availableBillingTypeOptions
      }
    ],
    showValidationErrors:
      pricingEditorContext.queries.rawData.editor.showValidationErrors,
    disabled:
      !pricingEditorContext.queries.availableFeatures.common.form.available
        .enabled,
    onChange: newData => {
      pricingEditorContext.mutators.common.updatePricingEditorData({
        common: {
          name: newData.name,
          currency: newData.currency
        },
        STANDARD: {
          price: newData.price,
          billingFrequency: newData.billingFrequency,
          billingType: newData.billingType
        }
      })
    }
  })

  useSyncFormValidity({
    isFormValid: formQueries.isValid,
    formValidationResults: formQueries.validationResults,
    additionalFields: ['billingFrequency', 'billingType']
  })

  const enhancedFields = useMemo(() => {
    return {
      ...fields,
      currency: {
        ...fields.currency,
        hidden: Boolean(
          pricingEditorContext.queries.rawData.configuration.currency
        )
      },
      billingFrequency: {
        ...fields.billingFrequency,
        /**
         * Adjust the price on a frequency change.
         */
        onChange: (newValue: BillingFrequency) => {
          const originalBillingFrequency = formQueries.formData.billingFrequency

          const ratio = getBillingFrequencyMultiplier({
            fromFrequency: originalBillingFrequency,
            toFrequency: newValue
          })

          const newPrice = adjustStandardPrice({
            ratio,
            price: formQueries.formData.price
          })

          formMutators.updateFormData({
            price: newPrice.toString(),
            billingFrequency: newValue
          })
        }
      }
    }
  }, [
    fields,
    pricingEditorContext.queries.rawData.configuration.currency,
    formQueries,
    formMutators
  ])

  const priceDetails = useMemo(() => {
    return commonPriceDetails({
      taxCategoryName:
        pricingEditorContext.queries.rawData.data.product.taxCategoryName,
      revenueRecognitionMethod:
        pricingEditorContext.queries.rawData.data.product
          .revenueRecognitionMethod,
      pricingModel: 'STANDARD',
      billingFrequency: formQueries.formData.billingFrequency,
      billingType: formQueries.formData.billingType
    })
  }, [
    formQueries.formData.billingFrequency,
    formQueries.formData.billingType,
    pricingEditorContext.queries.rawData.data.product.taxCategoryName,
    pricingEditorContext.queries.rawData.data.product.revenueRecognitionMethod
  ])

  return {
    fieldsConfig: enhancedFields,
    priceDetails
  }
}
